import React, { useState, useEffect, useRef } from 'react';
import StepLayout from '../components/StepLayout';
import StepCaptchaViewer from '../components/StepCaptchaViewer';
import CodeEntryPanel from '../components/CodeEntryPanel';
import ApproveOnPhone from '../components/ApproveOnPhone';
import { api } from '../api';
import { connectSSE } from '../sse';

// 2FA v2: agent-driven mode detection. The V3 backend classifies the
// LinkedIn challenge into one of three SSE events; we render the right
// surface per mode without a manual seller toggle.
//
//   needs_otp         → CodeEntryPanel variant='otp'
//   needs_email_code  → CodeEntryPanel variant='email_code'
//   needs_app_approval → ApproveOnPhone (no input, no button — V3 polls)
//
// Defensive default: if a stale `needs_code` event arrives (BE not yet
// upgraded), fall back to OTP. The OTP copy "authenticator app or SMS"
// covers the common case; if LinkedIn actually wants an email code, the
// 6-digit input shape is identical and submission still works.
export default function Step05TwoFA({ step, totalSteps, sessionId, sessionData, updateData, onNext, goToStep }) {
  const initialMode = (() => {
    const m = sessionData?.twoFAMode;
    if (m === 'otp' || m === 'email_code' || m === 'app_approval') return m;
    // Back-compat: v1 wrote `twoFAMethod: 'app'` on app-approval. Honour it.
    if (sessionData?.twoFAMethod === 'app') return 'app_approval';
    return 'otp';
  })();

  const [twoFAMode, setTwoFAMode] = useState(initialMode);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [captchaActive, setCaptchaActive] = useState(false);
  const sseRef = useRef(null);
  const submittedRef = useRef(false);
  const advancedRef = useRef(false);
  const codeInputRef = useRef(null);

  // iter-39: shared reset for re-issued challenges. LinkedIn rejects
  // a wrong code by re-presenting the OTP screen; V3 picks that up
  // and fires another needs_otp / needs_email_code event. Before iter
  // -39, only the legacy needs_code event triggered a reset, so a
  // wrong code on a modern V3 build left the FE permanently stuck on
  // 'Verifying with LinkedIn...'.
  const resetAfterRejectedCode = () => {
    if (!submittedRef.current) return;
    submittedRef.current = false;
    setVerifying(false);
    setLoading(false);
    setCode('');
    if (codeInputRef.current?.clear) codeInputRef.current.clear();
    // B44 (2026-05-15): LinkedIn EPC codes expire in ~5 min. Most
    // rejections are expiry, not typo — tell the seller to check
    // their inbox for the LATEST code rather than retyping.
    setError(
      'Code didn\'t work. LinkedIn just sent a fresh one. Open your '
      + 'inbox, find the most recent code, and enter that.',
    );
  };

  // Self-recover: if Step 5 was reached because Step 4 advanced on
  // an SSE event but login was actually already done (e.g., V3 saw
  // /feed/ at iter=1 and emitted login_success before we got here),
  // poll /status once and skip directly to Step 6.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const body = await api.getStatus(sessionId);
        if (cancelled) return;
        if (body?.currentStep && body.currentStep >= 6 && !advancedRef.current) {
          advancedRef.current = true;
          if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
          if (goToStep) {
            goToStep(7);
          } else {
            onNext();
          }
        }
      } catch (_) { /* transient */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, goToStep, onNext]);

  // SSE: variant classification + login_success advance. Same gating as
  // v1 — only login_success advances, not v3_done (scrape isn't committed
  // until login_success fires).
  useEffect(() => {
    if (!sessionId) return;
    sseRef.current = connectSSE(sessionId, (event) => {
      const t = event.type;
      if (t === 'login_success') {
        if (!advancedRef.current) {
          advancedRef.current = true;
          if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
          onNext();
        }
      } else if (t === 'v3_done') {
        setVerifying(true);
      } else if (t === 'needs_otp') {
        setTwoFAMode('otp');
        // iter-39: if we already submitted a code, this is a
        // re-presented challenge = rejection. Reset the input
        // surface so the seller can type a fresh code.
        if (submittedRef.current) {
          resetAfterRejectedCode();
        } else {
          setError('');
        }
      } else if (t === 'needs_email_code') {
        setTwoFAMode('email_code');
        if (submittedRef.current) {
          resetAfterRejectedCode();
        } else {
          setError('');
        }
      } else if (t === 'needs_app_approval') {
        // Pure UI switch — V3 driver polls for the redirect on its own.
        setTwoFAMode('app_approval');
        setError('');
      } else if (t === 'needs_code') {
        // Legacy event — older BE bundles haven't been redeployed.
        // Default to OTP (covers authenticator + SMS + email cases).
        setTwoFAMode((prev) => (prev === 'app_approval' ? prev : 'otp'));
        if (submittedRef.current) {
          resetAfterRejectedCode();
        }
      } else if (t === 'v3_blocked' || t === 'login_failed' || t === 'error') {
        const reason = event.reason || event.message || event.error || '';
        let msg = reason || 'Login failed.';
        if (typeof reason === 'string' && reason.startsWith('app_approval_timeout')) {
          msg = 'We didn\'t receive an approval from your LinkedIn app. '
              + 'Please try again, you may need to open the LinkedIn '
              + 'mobile app to receive the next push notification.';
        }
        submittedRef.current = false;
        setVerifying(false);
        setLoading(false);
        setError(msg);
      } else if (t === 'code_received') {
        setVerifying(true);
      } else if (t === 'needs_captcha') {
        setCaptchaActive(true);
      } else if (t === 'captcha_solved') {
        setCaptchaActive(false);
      } else if (t === 'captcha_failed') {
        setCaptchaActive(false);
        const reason = event.reason || 'Verification timed out';
        setError(`Verification failed: ${reason}`);
        setLoading(false);
        setVerifying(false);
        submittedRef.current = false;
      }
    });
    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [sessionId, onNext]);

  // Persist the resolved mode so navigating away + back (or reaching
  // Step 5 fresh after a reload) doesn't drop the classification.
  useEffect(() => {
    if (updateData) updateData({ twoFAMode });
  }, [twoFAMode, updateData]);

  const handleCodeComplete = (enteredCode) => {
    setCode(enteredCode);
  };

  const handleSubmitCode = async () => {
    if (code.length < 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    setLoading(true);
    setError('');
    submittedRef.current = true;
    try {
      // Backend route accepts only `method: 'otp'` for typed codes — the
      // schema variant is shared between OTP, SMS, and email codes
      // because they all RPUSH the same key. The variant lives in the
      // SSE event tag, not in the submission payload.
      await api.submit2FA(sessionId, { code, method: 'otp' });
      setVerifying(true);
    } catch (err) {
      submittedRef.current = false;
      setError(err.message || 'Invalid code. Please try again.');
      setLoading(false);
      setVerifying(false);
    }
  };

  // iter-40: Resend Code button removed per operator. LinkedIn drives
  // its own retry — the seller can't actually trigger another code from
  // our side; the button was misleading. If LinkedIn re-presents the
  // challenge, V3 fires another needs_otp / needs_email_code and the
  // input resets via resetAfterRejectedCode().

  if (captchaActive) {
    return (
      <StepLayout
        title={null}
      >
        <StepCaptchaViewer
          sessionId={sessionId}
          onError={(reason) => {
            setCaptchaActive(false);
            setError(`Verification could not be loaded: ${reason}`);
          }}
        />
      </StepLayout>
    );
  }

  return (
    <StepLayout title="Two-Factor Authentication" variant="centered">
      <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 20, textAlign: 'center' }}>
        LinkedIn requires additional verification to complete the login.
      </p>

      {twoFAMode === 'app_approval' ? (
        <ApproveOnPhone
          subtitle={"We'll continue automatically once LinkedIn confirms your sign-in."}
          error={error}
        />
      ) : (
        <CodeEntryPanel
          ref={codeInputRef}
          variant={twoFAMode === 'email_code' ? 'email_code' : 'otp'}
          code={code}
          onCodeComplete={handleCodeComplete}
          onSubmit={handleSubmitCode}
          loading={loading}
          verifying={verifying}
          error={error}
        />
      )}
    </StepLayout>
  );
}
